

export const dynamic = 'force-dynamic';

import { useState, useEffect } from "react";
import VendorReportsLayout from "../VendorReportsLayout";
import { reportsApi } from "@/lib/api";

// ─── Types ────────────────────────────────────────────────────────────────────

interface VendorPerformance {
  id: number;
  name: string;
  vendor_type?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  total_events: number;
  status_breakdown: {
    completed: number;
    cancelled: number;
    pending: number;
    confirmed: number;
    in_progress: number;
    paid: number;
  };
  completion_rate: number;
  reliability_score: number;
  total_commission: number;
  average_commission: number;
  current_balance: number;
  total_revenue?: number;
  total_cost?: number;
  total_paid?: number;
  outstanding_balance?: number;
  payment_rate?: number;
}

interface PerformanceSummary {
  total_vendors: number;
  total_events: number;
  total_completed: number;
  total_cancelled: number;
  average_completion_rate: number;
  average_reliability_score: number;
  total_revenue?: number;
  total_cost?: number;
  total_paid?: number;
  total_commission?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n ?? 0);

// ─── Page Component ───────────────────────────────────────────────────────────

export default function VendorPerformancePage() {
  const [period, setPeriod] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [vendors, setVendors] = useState<VendorPerformance[]>([]);
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsApi
      .vendorPerformance(period.from && period.to ? { from_date: period.from, to_date: period.to } : undefined)
      .then((res: any) => {
        if (res?.success) {
          setVendors(res.data.vendors || []);
          setSummary(res.data.summary || null);
        }
      })
      .catch((err) => console.error("Failed to load vendor performance:", err))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <VendorReportsLayout onPeriodChange={setPeriod}>
      {/* ── Summary Cards ── */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-stone-800">{summary.total_vendors || 0}</div>
            <div className="text-xs text-stone-400">Active Vendors</div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-emerald-700">{summary.total_events || 0}</div>
            <div className="text-xs text-stone-400">Total Events</div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-amber-700">{(summary.average_completion_rate || 0).toFixed(1)}%</div>
            <div className="text-xs text-stone-400">Avg Completion Rate</div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-violet-700">{(summary.average_reliability_score || 0).toFixed(1)}%</div>
            <div className="text-xs text-stone-400">Avg Reliability Score</div>
          </div>
        </div>
      )}

      {/* ── Performance Table ── */}
      <div className="bg-white rounded-2xl border border-stone-200">
        <div className="px-5 py-4 border-b border-stone-100">
          <h3 className="font-bold text-stone-800 font-serif text-sm">Vendor Performance Metrics</h3>
        </div>

        {loading ? (
          <div className="p-5 space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-stone-50 rounded-xl" />
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="p-8 text-center text-sm text-stone-400 italic">No vendor data found for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Vendor</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-stone-500 uppercase">Events</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-stone-500 uppercase">Completed</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-stone-500 uppercase">Cancelled</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-stone-500 uppercase">Completion %</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-stone-500 uppercase">Reliability %</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold text-stone-500 uppercase">Commission</th>
                  <th className="text-right px-3 py-3 text-xs font-semibold text-stone-500 uppercase">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {vendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-stone-800">{vendor.name}</div>
                      {vendor.vendor_type && <div className="text-xs text-stone-400">{vendor.vendor_type}</div>}
                    </td>
                    <td className="px-3 py-3 text-center font-semibold text-stone-700">{vendor.total_events}</td>
                    <td className="px-3 py-3 text-center text-emerald-600">{vendor.status_breakdown.completed}</td>
                    <td className="px-3 py-3 text-center text-rose-600">{vendor.status_breakdown.cancelled}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        vendor.completion_rate >= 80 ? "bg-emerald-100 text-emerald-700" :
                        vendor.completion_rate >= 50 ? "bg-amber-100 text-amber-700" :
                        "bg-rose-100 text-rose-700"
                      }`}>
                        {vendor.completion_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        vendor.reliability_score >= 80 ? "bg-emerald-100 text-emerald-700" :
                        vendor.reliability_score >= 50 ? "bg-amber-100 text-amber-700" :
                        "bg-rose-100 text-rose-700"
                      }`}>
                        {vendor.reliability_score.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-violet-700">{fmt(vendor.total_commission)}</td>
                    <td className="px-3 py-3 text-right">
                      <span className={vendor.current_balance > 0 ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                        {fmt(vendor.current_balance)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </VendorReportsLayout>
  );
}
